import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Package, Search, CheckCircle, XCircle, Clock, Loader2, Store,
  Image as ImageIcon, IndianRupee, AlertTriangle, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import { formatPrice } from '../../lib/utils';

export default function AdminProductApprovals() {
  const [products, setProducts] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/products/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products || []);
      setCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (productId) => {
    setActionLoading(productId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/products/${productId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Product approved!');
      fetchProducts();
    } catch (error) {
      console.error('Error approving product:', error);
      toast.error('Failed to approve product');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (productId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setActionLoading(productId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/products/${productId}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Product rejected');
      setShowRejectModal(null);
      setRejectReason('');
      fetchProducts();
    } catch (error) {
      console.error('Error rejecting product:', error);
      toast.error('Failed to reject product');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredProducts = products.filter(product => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(search) ||
      product.sku?.toLowerCase().includes(search) ||
      product.vendor?.business_name?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-6 space-y-6" data-testid="admin-product-approvals">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-7 h-7 text-purple-600" />
            Product Approvals
          </h1>
          <p className="text-gray-500 mt-1">Review and approve vendor products before they go live</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{counts.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold text-green-600">{counts.approved}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{counts.rejected}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          placeholder="Search products by name, SKU, or vendor..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="product-search"
        />
      </div>

      {/* Products List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
            <p className="text-gray-500">No products pending approval</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden" data-testid={`product-card-${product.id}`}>
              <CardContent className="p-0">
                {/* Product Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Product Image */}
                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {product.images && product.images.length > 0 ? (
                        <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-300" />
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900">{product.name}</h3>
                      <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Store className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          {product.vendor?.business_name || 'Unknown Vendor'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{formatPrice(product.price)}</p>
                      <p className="text-sm text-gray-500">Stock: {product.inventory}</p>
                    </div>
                    <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">
                      <Clock className="w-3 h-3 mr-1" />
                      Pending
                    </Badge>
                    {expandedProduct === product.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedProduct === product.id && (
                  <div className="border-t bg-gray-50 p-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Product Details */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-700">Product Details</h4>
                        <div className="space-y-2 text-sm">
                          <p className="text-gray-600">
                            <span className="text-gray-400">Description:</span><br />
                            {product.description || 'No description'}
                          </p>
                          <p className="text-gray-600">
                            <span className="text-gray-400">Category:</span> {product.category_id || 'Not set'}
                          </p>
                          {product.region_name && (
                            <p className="text-gray-600">
                              <span className="text-gray-400">Region:</span> {product.region_name}
                            </p>
                          )}
                          {product.tags && product.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {product.tags.map((tag, i) => (
                                <span key={i} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Vendor Info */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-700">Vendor Info</h4>
                        <div className="p-3 bg-white rounded-lg border">
                          <p className="font-medium text-gray-900">{product.vendor?.business_name}</p>
                          <p className="text-sm text-gray-500">{product.vendor?.owner_name}</p>
                          <p className="text-sm text-gray-500">{product.vendor?.phone}</p>
                        </div>
                      </div>
                    </div>

                    {/* Images Gallery */}
                    {product.images && product.images.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-700 mb-2">Product Images</h4>
                        <div className="flex gap-2 overflow-x-auto">
                          {product.images.map((img, i) => (
                            <img key={i} src={img} alt={`Product ${i+1}`} className="w-20 h-20 object-cover rounded-lg border" />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-4 flex gap-2">
                      <Button
                        onClick={() => handleApprove(product.id)}
                        disabled={actionLoading === product.id}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-btn-${product.id}`}
                      >
                        {actionLoading === product.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Approve Product
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => setShowRejectModal(product.id)}
                        disabled={actionLoading === product.id}
                        data-testid={`reject-btn-${product.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                      <Button variant="outline" onClick={() => window.open(`/product/${product.id}`, '_blank')}>
                        <Eye className="w-4 h-4 mr-2" />
                        Preview
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Reject Product
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Please provide a reason for rejecting this product. The vendor will see this reason.
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full p-3 border rounded-lg min-h-[100px] focus:ring-2 focus:ring-red-200 focus:border-red-400"
                data-testid="reject-reason-input"
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(null);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleReject(showRejectModal)}
                  disabled={actionLoading === showRejectModal || !rejectReason.trim()}
                  data-testid="confirm-reject-btn"
                >
                  {actionLoading === showRejectModal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Confirm Rejection
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
